import { isFetchResultSuccessful } from '@/entities/FetchResult'
import { getHourBalancesApiCall } from '@/entities/HourBalances/HourBalancesService'
import { getLeaveOfAbsences } from '@/helpers/api-ts/leaveOfAbsence'
import { getLoketLeaveBalance } from '@/helpers/api-ts/loketLeaveBalance'
import { getProfileOfCurrentUser } from '@/helpers/api-ts/userApi'
import { getEndOfWorkDay, getStartOfWorkDay } from '@/helpers/date'
import { isObjEmpty, roundNumberDecimals, roundNumberToRoundedNumberToQuarter } from '@/helpers/tools'
import { LeaveType, PayrollPeriod } from '@/models'
import { TypedDispatch } from '@/redux/store'
import { getHoursFromLeaveOfAbsences } from '@/services/LeaveOfAbsenceHourService/getHoursFromLeaveOfAbsences'

import { setError, setHourBalances, setIsLoading } from './hourBalancesReducer'

export const getHourBalance = (
    payrollPeriod: PayrollPeriod,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    const startDate = payrollPeriod.start
    const endDate = payrollPeriod.end.date
    try {
        const result = await getHourBalancesApiCall({
            startDate,
        })

        if (! isFetchResultSuccessful(result)) {
            dispatch(setError(result.error))
            return
        }

        if (isObjEmpty(result.data) || result.data.length === 0) {
            dispatch(setError('error'))
            return
        }

        const user = result.data[0]
        const profilesResult = await getProfileOfCurrentUser([]) // isEligibleForTimeForTime

        const userWithLoketId = {
            ...user,
            loketEmployeeId: profilesResult.loketEmployeeId,
            isEligibleForTimeForTime: profilesResult.isEligibleForTimeForTime,
        }

        const loketBalance = userWithLoketId.loketEmployeeId !== null
            ? await getLoketLeaveBalance(userWithLoketId.loketEmployeeId)
            : undefined
        

        const leaveOfAbsences = await getLeaveOfAbsences({
            userId: user.id,
            startDate: getStartOfWorkDay(startDate),
            endDate: getEndOfWorkDay(endDate),
            status: [1, 2],
            includes: ['leaveOfAbsenceHours', 'leaveOfAbsenceHours.payrollPeriod', 'user'],
        })

        const usedTvtHours = getHoursFromLeaveOfAbsences(
            leaveOfAbsences,
            payrollPeriod.id,
            [LeaveType.tvt],
        )

        const vacancyLoaHours = getHoursFromLeaveOfAbsences(leaveOfAbsences, payrollPeriod.id, [LeaveType.normal])
        const specialLoaHours = getHoursFromLeaveOfAbsences(leaveOfAbsences, payrollPeriod.id, [LeaveType.special])
        const tvtLoaHours = getHoursFromLeaveOfAbsences(leaveOfAbsences, payrollPeriod.id, [LeaveType.tvt])

        const data = {
            userId: user.id,
            userName: user.name,
            contractHours: roundNumberToRoundedNumberToQuarter(user.contractHours),
            workedHours: roundNumberToRoundedNumberToQuarter(user.workedHours),
            absenceHours: roundNumberDecimals(user.absenceHours),
            leaveOfAbsenceHours: roundNumberDecimals(user.leaveHours),
            vacancyLoaHours: roundNumberDecimals(vacancyLoaHours),
            specialLoaHours: roundNumberDecimals(specialLoaHours),
            usedTvtHours: roundNumberDecimals(usedTvtHours),
            tvtLoaHours: roundNumberDecimals(tvtLoaHours),
            mutatedTvtHours: roundNumberToRoundedNumberToQuarter(user.tvtMutation),
            leaveOfAbsenceBalance: roundNumberToRoundedNumberToQuarter(loketBalance?.leaveBalanceTotal || 0),
            tvtBalance: roundNumberToRoundedNumberToQuarter(loketBalance?.timeForTimeTotal || 0),
            isEligibleForTimeForTime: userWithLoketId.isEligibleForTimeForTime,
        }

        dispatch(setHourBalances([data]))
    } catch (error) {
        console.error('[getHourBalances]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}